import { authFetch, getCsrfHeaders } from '../../api/csrf';
import React, { useState, useRef } from 'react';
import {
    X, Upload, FileText, AlertCircle, CheckCircle2,
    Download, Loader2, Copy, Search
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from './Button/Button';
import * as XLSX from 'xlsx';

// Utility for parsing CSV string
const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];
        if (char === '"') {
            if (inQuotes && nextChar === '"') {
                current += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (char === ',' && !inQuotes) {
            values.push(current);
            current = '';
        } else {
            current += char;
        }
    }
    values.push(current);
    return values;
};

const parseCSV = (csvContent) => {
    try {
        const lines = csvContent.split(/\r?\n/).filter(line => line.trim());
        if (lines.length < 2) {
            return { success: false, error: 'CSV must contain a header row and at least one data row' };
        }
        const headers = parseCSVLine(lines[0]);
        const records = [];
        for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i]);
            if (values.length === 0 || values.every(v => !v.trim())) continue;
            const record = {};
            headers.forEach((header, index) => {
                record[header.trim()] = values[index]?.trim() || '';
            });
            records.push(record);
        }
        return { success: true, records };
    } catch (error) {
        return { success: false, error: error.message || 'Failed to parse CSV' };
    }
};

const emptyProgress = () => ({
    current: 0,
    total: 0,
    percent: 0,
    created: 0,
    updated: 0,
    skipped: 0,
    failed: 0,
    stage: 'idle',
});

const BulkUploadModal = ({ isOpen = true, onClose, onSuccess, config }) => {
    const {
        title = 'Bulk Upload',
        subtitle = 'Import multiple records using a CSV file',
        templateEndpoint,
        validateEndpoint,
        uploadEndpoint,
        templateFilename = 'template.csv',
        dataKey = 'data',
        maxRecords = 500,
        maxFileSizeMB = null, // null/0 = no file-size limit; only row count is enforced
        uploadChunkSize = 250,
        instructions = [],
        allowedFormats = [],
        accept = null
    } = config || {};

    const [file, setFile] = useState(null);
    const [isParsing, setIsParsing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [validationResults, setValidationResults] = useState(null);
    const [uploadResults, setUploadResults] = useState(null);
    const [skipDuplicates, setSkipDuplicates] = useState(true);
    const [step, setStep] = useState(1);
    const [uploadProgress, setUploadProgress] = useState(emptyProgress());
    const [errorSearch, setErrorSearch] = useState('');

    const fileInputRef = useRef(null);
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const hasFileSizeLimit = maxFileSizeMB != null && Number(maxFileSizeMB) > 0;
    const maxFileBytes = hasFileSizeLimit ? Number(maxFileSizeMB) * 1024 * 1024 : null;
    const chunkSize = Math.max(50, Number(uploadChunkSize) || 250);

    const getAuthHeaders = () => ({
        'Content-Type': 'application/json',
        ...getCsrfHeaders(),
    });

    const clearSelectedFile = () => {
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const enforceRecordLimit = (records) => {
        if (records.length > maxRecords) {
            toast.error(
                `The file has ${records.length.toLocaleString()} rows, which exceeds the maximum of ${maxRecords.toLocaleString()} per upload. Please split the file.`
            );
            clearSelectedFile();
            return false;
        }
        return true;
    };

    const processSelectedFile = (selectedFile) => {
        if (!selectedFile) return;

        const isCSV = selectedFile.name.endsWith('.csv');
        const isExcel = selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls');

        const isExcelOnly = allowedFormats.includes('excel') || accept === '.xlsx,.xls';
        if (isExcelOnly) {
            if (!isExcel) {
                toast.error('Please select an Excel file (.xlsx or .xls)');
                return;
            }
        } else if (!isCSV && !isExcel) {
            toast.error('Please select a CSV or Excel file');
            return;
        }
        if (hasFileSizeLimit && selectedFile.size > maxFileBytes) {
            toast.error(`File size should be less than ${maxFileSizeMB}MB`);
            return;
        }

        setFile(selectedFile);
        setUploadProgress(emptyProgress());
        if (isCSV) {
            handleCSVFile(selectedFile);
        } else {
            handleExcelFile(selectedFile);
        }
    };

    const handleFileChange = (e) => {
        processSelectedFile(e.target.files?.[0]);
    };

    const handleCSVFile = async (selectedFile) => {
        setIsParsing(true);
        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const text = e.target.result;
                const result = parseCSV(text);
                if (result.success) {
                    if (!enforceRecordLimit(result.records)) {
                        setIsParsing(false);
                        return;
                    }
                    setParsedData(result.records);
                    setStep(2);
                    validateData(result.records);
                } else {
                    toast.error(result.error || 'Failed to parse CSV');
                    clearSelectedFile();
                }
                setIsParsing(false);
            };
            reader.onerror = () => {
                toast.error('Failed to read file');
                clearSelectedFile();
                setIsParsing(false);
            };
            reader.readAsText(selectedFile);
        } catch (error) {
            toast.error('An error occurred while parsing the CSV file');
            clearSelectedFile();
            setIsParsing(false);
        }
    };

    const handleExcelFile = async (selectedFile) => {
        setIsParsing(true);
        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const records = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

                    if (records.length === 0) {
                        toast.error('Excel file must contain at least one data row');
                        clearSelectedFile();
                        setIsParsing(false);
                        return;
                    }
                    if (!enforceRecordLimit(records)) {
                        setIsParsing(false);
                        return;
                    }

                    // Trim keys and values
                    const trimmedRecords = records.map(record => {
                        const trimmedRecord = {};
                        Object.keys(record).forEach(key => {
                            const trimmedKey = String(key).trim();
                            const val = record[key];
                            trimmedRecord[trimmedKey] = typeof val === 'string' ? val.trim() : String(val);
                        });
                        return trimmedRecord;
                    });

                    setParsedData(trimmedRecords);
                    setStep(2);
                    validateData(trimmedRecords);
                } catch (err) {
                    toast.error('Failed to parse Excel file');
                    clearSelectedFile();
                } finally {
                    setIsParsing(false);
                }
            };
            reader.onerror = () => {
                toast.error('Failed to read file');
                clearSelectedFile();
                setIsParsing(false);
            };
            reader.readAsArrayBuffer(selectedFile);
        } catch (error) {
            toast.error('An error occurred while parsing the Excel file');
            clearSelectedFile();
            setIsParsing(false);
        }
    };

    const validateData = async (data) => {
        setIsValidating(true);
        try {
            const response = await authFetch(`${API_BASE_URL}${validateEndpoint}`, {
                credentials: 'include',
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ [dataKey]: data })
            });
            const result = await response.json();
            if (result.status === 'success' || result.responseCode === 200) {
                const payload = result.data || {};
                setValidationResults({
                    ...payload,
                    valid: payload.valid ?? payload.validRows ?? 0,
                    invalid: payload.invalid ?? payload.failed ?? 0,
                    duplicate: payload.duplicate ?? payload.duplicates?.length ?? 0,
                });
            } else {
                toast.error(result.message || result.responseMessage || 'Validation failed');
            }
        } catch (error) {
            toast.error('An error occurred during validation');
        } finally {
            setIsValidating(false);
        }
    };

    const uploadChunk = async (chunk) => {
        const response = await authFetch(`${API_BASE_URL}${uploadEndpoint}`, {
            credentials: 'include',
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ [dataKey]: chunk, skipDuplicates }),
        });
        const result = await response.json();
        if (response.ok && (result.status === 'success' || result.responseCode === 200 || result.responseCode === 201)) {
            return { success: true, data: result.data || {} };
        }
        return {
            success: false,
            message: result.message || result.responseMessage || 'Upload failed',
            data: result.data || result.error || {},
        };
    };

    const handleUpload = async () => {
        if (!parsedData.length) return;
        if (parsedData.length > maxRecords) {
            toast.error(`Maximum ${maxRecords.toLocaleString()} records allowed per upload`);
            return;
        }

        setIsUploading(true);
        setStep('uploading');
        setUploadProgress({
            current: 0,
            total: parsedData.length,
            percent: 0,
            created: 0,
            updated: 0,
            skipped: 0,
            failed: 0,
            stage: 'uploading',
        });

        try {
            const aggregated = {
                total: parsedData.length,
                created: 0,
                updated: 0,
                skipped: 0,
                failed: 0,
                errors: [],
                duplicates: [],
            };

            for (let i = 0; i < parsedData.length; i += chunkSize) {
                const chunk = parsedData.slice(i, i + chunkSize);
                const chunkResult = await uploadChunk(chunk);

                if (chunkResult.success) {
                    const data = chunkResult.data;
                    aggregated.created += Number(data.created) || 0;
                    aggregated.updated += Number(data.updated) || 0;
                    aggregated.skipped += Number(data.skipped) || 0;
                    aggregated.failed += Number(data.failed) || 0;
                    if (Array.isArray(data.errors)) {
                        aggregated.errors.push(...data.errors);
                    }
                    if (Array.isArray(data.duplicates)) {
                        aggregated.duplicates.push(...data.duplicates);
                    }
                } else {
                    aggregated.failed += chunk.length;
                    aggregated.errors.push(chunkResult.message || `Chunk starting at row ${i + 1} failed`);
                    if (chunkResult.data?.errors && Array.isArray(chunkResult.data.errors)) {
                        aggregated.errors.push(...chunkResult.data.errors);
                    }
                }

                const current = Math.min(i + chunk.length, parsedData.length);
                setUploadProgress({
                    current,
                    total: parsedData.length,
                    percent: Math.round((current / parsedData.length) * 100),
                    created: aggregated.created,
                    updated: aggregated.updated,
                    skipped: aggregated.skipped,
                    failed: aggregated.failed,
                    stage: 'uploading',
                });
            }

            aggregated.errors = aggregated.errors.slice(0, 50);
            aggregated.duplicates = aggregated.duplicates.slice(0, 50);
            setUploadResults(aggregated);
            setUploadProgress((prev) => ({ ...prev, percent: 100, stage: 'done' }));
            setStep(3);
            toast.success('Bulk upload completed');
        } catch (error) {
            toast.error('An error occurred during upload');
            setStep(2);
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await authFetch(`${API_BASE_URL}${templateEndpoint}`, {
                credentials: 'include',
                method: 'GET',
                headers: {
                }
            });
            if (!response.ok) throw new Error('Failed to download template');

            let downloadName = templateFilename;
            const disposition = response.headers.get('content-disposition') || response.headers.get('Content-Disposition');
            if (disposition && disposition.includes('filename=')) {
                const match = disposition.match(/filename=["']?([^"';\n]+)["']?/);
                if (match && match[1]) {
                    downloadName = match[1].trim();
                }
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = downloadName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Failed to download template');
        }
    };

    const reset = () => {
        setFile(null);
        setParsedData([]);
        setValidationResults(null);
        setUploadResults(null);
        setUploadProgress(emptyProgress());
        setIsUploading(false);
        setIsValidating(false);
        setIsParsing(false);
        setErrorSearch('');
        setStep(1);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleClose = () => {
        if (isUploading) {
            toast.error('Please wait for the upload to finish');
            return;
        }
        reset();
        onClose?.();
    };

    const formatValidationMessage = (item) => {
        if (item == null) return '';
        if (typeof item === 'string') return item;
        if (item.row != null) {
            const detail = Array.isArray(item.errors)
                ? item.errors.join(', ')
                : (item.errors || item.reason || item.message || JSON.stringify(item));
            return `Row ${item.row}: ${detail}`;
        }
        return item.message || item.reason || JSON.stringify(item);
    };

    const validationErrorMessages = (validationResults?.errors || [])
        .map(formatValidationMessage)
        .filter(Boolean);
    const validationDuplicateMessages = (validationResults?.duplicates || [])
        .map(formatValidationMessage)
        .filter(Boolean);

    const searchLower = errorSearch.trim().toLowerCase();
    const filteredErrorMessages = searchLower
        ? validationErrorMessages.filter((msg) => msg.toLowerCase().includes(searchLower))
        : validationErrorMessages;
    const filteredDuplicateMessages = searchLower
        ? validationDuplicateMessages.filter((msg) => msg.toLowerCase().includes(searchLower))
        : validationDuplicateMessages;

    const copyValidationLog = async () => {
        const allLines = [
            ...validationErrorMessages,
            ...validationDuplicateMessages.map((m) => `DUPLICATE: ${m}`),
        ];
        if (!allLines.length) return;
        try {
            await navigator.clipboard.writeText(allLines.join('\n'));
            toast.success(`Copied ${allLines.length.toLocaleString()} validation message(s)`);
        } catch {
            toast.error('Failed to copy validation log');
        }
    };

    const downloadValidationLog = () => {
        const allLines = [
            ...validationErrorMessages,
            ...validationDuplicateMessages.map((m) => `DUPLICATE: ${m}`),
        ];
        if (!allLines.length) return;
        const blob = new Blob([allLines.join('\n')], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bulk_upload_validation_errors.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Validation error log downloaded');
    };

    const readyCount = Number(validationResults?.valid ?? validationResults?.validRows ?? 0);
    const errorCount = Number(validationResults?.invalid ?? validationResults?.failed ?? 0);
    const duplicateCount = Number(
        validationResults?.duplicate ?? validationResults?.duplicates?.length ?? 0
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm !mt-0">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                        <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
                    </div>
                    <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" disabled={isUploading}>
                        <X className="h-6 w-6 text-gray-400 hover:text-gray-600" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                    {step === 1 && (
                        <div className="max-w-3xl mx-auto space-y-8 py-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white p-5 rounded-xl border border-blue-100 shadow-sm transition-all hover:shadow-md">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-blue-50 rounded-lg">
                                            <Download className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <span className="font-semibold text-gray-900">1. Download Template</span>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                                        Get our template with the correct column structure for data.
                                    </p>
                                    {instructions && !Array.isArray(instructions) && instructions.download && instructions.download.length > 0 && (
                                        <ul className="text-xs text-gray-600 space-y-2 list-disc pl-4 mb-4 leading-relaxed">
                                            {instructions.download.map((inst, idx) => <li key={idx}>{inst}</li>)}
                                        </ul>
                                    )}
                                    <Button variant="outline" size="sm" icon={<Download className="h-4 w-4" />} onClick={handleDownloadTemplate} className="w-full justify-center text-blue-600 border-blue-200 hover:bg-blue-50">
                                        Download Template
                                    </Button>
                                </div>
                                <div className="bg-white p-5 rounded-xl border border-green-100 shadow-sm transition-all hover:shadow-md">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-green-50 rounded-lg">
                                            <FileText className="h-5 w-5 text-green-600" />
                                        </div>
                                        <span className="font-semibold text-gray-900">2. Prepare File</span>
                                    </div>
                                    {instructions ? (
                                        Array.isArray(instructions) ? (
                                            instructions.length > 0 ? (
                                                <ul className="text-xs text-gray-600 space-y-2 list-disc pl-4 leading-relaxed">
                                                    {instructions.map((inst, idx) => <li key={idx}>{inst}</li>)}
                                                </ul>
                                            ) : (
                                                <p className="text-sm text-gray-600 leading-relaxed">Open the template and fill in the required information.</p>
                                            )
                                        ) : (
                                            instructions.prepare && instructions.prepare.length > 0 ? (
                                                <ul className="text-xs text-gray-600 space-y-2 list-disc pl-4 leading-relaxed">
                                                    {instructions.prepare.map((inst, idx) => <li key={idx}>{inst}</li>)}
                                                </ul>
                                            ) : (
                                                <p className="text-sm text-gray-600 leading-relaxed">Open the template and fill in the required information.</p>
                                            )
                                        )
                                    ) : (
                                        <p className="text-sm text-gray-600 leading-relaxed">Open the template and fill in the required information.</p>
                                    )}
                                </div>
                            </div>
                            <div
                                className={`relative group border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-200 ${isParsing ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50/20'}`}
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onDrop={(e) => {
                                    e.preventDefault(); e.stopPropagation();
                                    processSelectedFile(e.dataTransfer.files?.[0]);
                                }}
                            >
                                <input type="file" ref={fileInputRef} onChange={handleFileChange} accept={accept || (allowedFormats.includes('excel') ? '.xlsx,.xls' : '.csv,.xlsx,.xls')} className="hidden" />
                                {isParsing ? (
                                    <div className="flex flex-col items-center">
                                        <Loader2 className="h-12 w-12 text-blue-500 animate-spin mb-4" />
                                        <p className="text-lg font-medium text-gray-900">Processing file...</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center">
                                        <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                                            <Upload className="h-8 w-8 text-blue-600" />
                                        </div>
                                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                            Click to select or drag and drop {allowedFormats.includes('excel') || accept === '.xlsx,.xls' ? 'Excel' : 'CSV or Excel'}
                                        </h4>
                                        <p className={`text-sm text-gray-500 ${hasFileSizeLimit ? 'mb-1' : 'mb-6'}`}>
                                            Maximum {maxRecords.toLocaleString()} records per upload
                                        </p>
                                        {hasFileSizeLimit && (
                                            <p className="text-sm text-gray-500 mb-6">Maximum file size: {maxFileSizeMB}MB</p>
                                        )}
                                        <Button variant="primary" onClick={() => fileInputRef.current?.click()} className="bg-blue-600 hover:bg-blue-700 px-8">Select File</Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {step === 2 && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm text-center">
                                    <div className="text-2xl font-bold text-gray-900">{parsedData.length.toLocaleString()}</div>
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Rows</div>
                                    <div className="text-[10px] text-gray-400 mt-1">Max {maxRecords.toLocaleString()}</div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm text-center">
                                    <div className="text-2xl font-bold text-green-600">
                                        {isValidating ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : readyCount.toLocaleString()}
                                    </div>
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Ready to Upload</div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm text-center">
                                    <div className="text-2xl font-bold text-red-500">
                                        {isValidating ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : errorCount.toLocaleString()}
                                    </div>
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Errors Found</div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm text-center">
                                    <div className="text-2xl font-bold text-yellow-500">
                                        {isValidating ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : duplicateCount.toLocaleString()}
                                    </div>
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Duplicates</div>
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded-xl border border-gray-200 flex items-center justify-between">
                                <div className="flex items-center gap-3 text-sm text-gray-700">
                                    <AlertCircle className="h-5 w-5 text-gray-400" />
                                    <span>How should we handle potential duplicate records?</span>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => setSkipDuplicates(true)} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${skipDuplicates ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Skip Duplicates</button>
                                    <button onClick={() => setSkipDuplicates(false)} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${!skipDuplicates ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Keep All</button>
                                </div>
                            </div>

                            {validationResults && (validationErrorMessages.length > 0 || validationDuplicateMessages.length > 0) && (
                                <div className="bg-white rounded-xl border border-red-100 overflow-hidden shadow-sm">
                                    <div className="px-4 py-3 border-b border-red-50 bg-red-50/60 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                        <div>
                                            <span className="text-sm font-semibold text-red-800">
                                                Row-wise Validation Errors
                                            </span>
                                            <p className="text-xs text-red-600 mt-0.5">
                                                Showing {filteredErrorMessages.length.toLocaleString()}
                                                {searchLower ? ` matching` : ''} of {validationErrorMessages.length.toLocaleString()} error(s)
                                                {validationDuplicateMessages.length > 0
                                                    ? ` · ${filteredDuplicateMessages.length.toLocaleString()} duplicate note(s)`
                                                    : ''}
                                            </p>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={copyValidationLog}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-red-200 text-red-700 hover:bg-red-50"
                                            >
                                                <Copy className="h-3.5 w-3.5" />
                                                Copy all
                                            </button>
                                            <button
                                                type="button"
                                                onClick={downloadValidationLog}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-red-200 text-red-700 hover:bg-red-50"
                                            >
                                                <Download className="h-3.5 w-3.5" />
                                                Download log
                                            </button>
                                        </div>
                                    </div>

                                    <div className="px-4 py-3 border-b border-gray-100">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                            <input
                                                type="text"
                                                value={errorSearch}
                                                onChange={(e) => setErrorSearch(e.target.value)}
                                                placeholder="Search by row number, phone, email, or message..."
                                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300"
                                            />
                                        </div>
                                    </div>

                                    <div className="max-h-[320px] overflow-y-auto p-3 space-y-2">
                                        {filteredErrorMessages.length === 0 && filteredDuplicateMessages.length === 0 ? (
                                            <p className="text-sm text-gray-500 px-2 py-4 text-center">
                                                No messages match your search.
                                            </p>
                                        ) : (
                                            <>
                                                {filteredErrorMessages.map((msg, idx) => (
                                                    <div key={`err-${idx}`} className="flex gap-3 text-sm text-red-700 bg-red-50 p-2.5 rounded-lg border border-red-100">
                                                        <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                                        <span className="break-words">{msg}</span>
                                                    </div>
                                                ))}
                                                {filteredDuplicateMessages.map((msg, idx) => (
                                                    <div key={`dup-${idx}`} className="flex gap-3 text-sm text-amber-700 bg-amber-50 p-2.5 rounded-lg border border-amber-100">
                                                        <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                                        <span className="break-words">{msg}</span>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {validationResults && errorCount > 0 && validationErrorMessages.length === 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                                    {errorCount.toLocaleString()} row(s) failed validation, but detailed messages were not returned.
                                    Please fix duplicates/invalid values and try again.
                                </div>
                            )}

                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                                    <span className="text-sm font-semibold text-gray-900">Data Preview (First 5 Rows)</span>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-gray-50/50">
                                                {parsedData.length > 0 && Object.keys(parsedData[0]).map(key => (
                                                    <th key={key} className="px-4 py-2.5 text-[11px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">{key}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {parsedData.slice(0, 5).map((row, i) => (
                                                <tr key={i} className="hover:bg-gray-50/30 transition-colors">
                                                    {Object.values(row).map((val, j) => (
                                                        <td key={j} className="px-4 py-2.5 text-xs text-gray-600 truncate max-w-[150px]">{val}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                    {step === 'uploading' && (
                        <div className="flex flex-col items-center justify-center py-16 px-8">
                            <Loader2 className="h-14 w-14 animate-spin text-blue-600 mb-6" />
                            <h3 className="text-xl font-bold text-gray-900">Uploading Records</h3>
                            <p className="text-gray-500 mt-2 mb-8 text-center">
                                Processing {uploadProgress.total.toLocaleString()} of max {maxRecords.toLocaleString()} records. Please keep this window open.
                            </p>

                            <div className="w-full max-w-md">
                                <div className="flex items-center justify-between text-sm font-medium text-gray-600 mb-2">
                                    <span>
                                        {uploadProgress.total > 0
                                            ? `${uploadProgress.current.toLocaleString()} of ${uploadProgress.total.toLocaleString()}`
                                            : 'Starting...'}
                                    </span>
                                    <span>{uploadProgress.percent || 0}%</span>
                                </div>
                                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-600 rounded-full transition-all duration-300"
                                        style={{ width: `${Math.min(uploadProgress.percent || 0, 100)}%` }}
                                    />
                                </div>
                                <div className="flex flex-wrap items-center justify-center gap-4 mt-4 text-xs font-medium">
                                    <span className="text-green-600">Created: {uploadProgress.created || 0}</span>
                                    <span className="text-cyan-600">Updated: {uploadProgress.updated || 0}</span>
                                    <span className="text-blue-600">Skipped: {uploadProgress.skipped || 0}</span>
                                    <span className="text-red-500">Failed: {uploadProgress.failed || 0}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    {step === 3 && (
                        <div className="text-center py-12 max-w-md mx-auto">
                            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                                <CheckCircle2 className="h-10 w-10 text-green-600" />
                            </div>
                            <h4 className="text-2xl font-bold text-gray-900 mb-2">Upload Completed!</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                                <div className="bg-green-50/50 p-4 rounded-xl border border-green-100">
                                    <div className="text-2xl font-bold text-green-600">{uploadResults?.created || 0}</div>
                                    <div className="text-xs font-semibold text-green-700 uppercase tracking-widest mt-1">Created</div>
                                </div>
                                {uploadResults?.updated !== undefined && (
                                    <div className="bg-cyan-50/50 p-4 rounded-xl border border-cyan-100">
                                        <div className="text-2xl font-bold text-cyan-600">{uploadResults?.updated || 0}</div>
                                        <div className="text-xs font-semibold text-cyan-700 uppercase tracking-widest mt-1">Updated</div>
                                    </div>
                                )}
                                <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                                    <div className="text-2xl font-bold text-blue-600">{uploadResults?.skipped || 0}</div>
                                    <div className="text-xs font-semibold text-blue-700 uppercase tracking-widest mt-1">Skipped</div>
                                </div>
                                <div className="bg-red-50/50 p-4 rounded-xl border border-red-100">
                                    <div className="text-2xl font-bold text-red-600">{uploadResults?.failed || 0}</div>
                                    <div className="text-xs font-semibold text-red-700 uppercase tracking-widest mt-1">Failed</div>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                                    <div className="text-2xl font-bold text-gray-600">{uploadResults?.total || 0}</div>
                                    <div className="text-xs font-semibold text-gray-700 uppercase tracking-widest mt-1">Total</div>
                                </div>
                            </div>
                            {Array.isArray(uploadResults?.errors) && uploadResults.errors.length > 0 && (
                                <div className="mb-6 flex flex-col gap-2">
                                    {uploadResults.errors.map((err, idx) => (
                                        <div key={idx} className="text-red-600 text-sm text-left">{err}</div>
                                    ))}
                                </div>
                            )}
                            <Button variant="primary" onClick={() => { onSuccess && onSuccess(); reset(); onClose(); }} className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg shadow-lg">Done</Button>
                        </div>
                    )}
                </div>

                {step !== 3 && step !== 'uploading' && (
                    <div className="px-6 py-4 border-t border-gray-100 bg-white flex justify-between items-center gap-4">
                        <Button variant="outline" onClick={step === 2 ? reset : handleClose} disabled={isUploading || isValidating || isParsing} className="px-6">
                            {step === 2 ? 'Upload Another' : 'Cancel'}
                        </Button>
                        {step === 2 && (
                            <div className="flex gap-3">
                                <Button
                                    variant="primary" onClick={handleUpload}
                                    disabled={isUploading || isValidating || (readyCount === 0 && duplicateCount === 0)}
                                    className="bg-blue-600 hover:bg-blue-700 px-10 min-w-32"
                                >
                                    {isUploading ? (
                                        <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /><span>Uploading...</span></div>
                                    ) : `Start Upload (${parsedData.length.toLocaleString()})`}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkUploadModal;
